// Klientská část Kolonie: drží model a podle režimu zapíná zvukovou a vizuální vrstvu.
import type { GameEvent } from "../../../games/types.ts";
import type { KolonieView } from "../../../games/kolonie/meta.ts";
import { TOPOLOGY as T } from "../../../games/kolonie/topology.ts";
import { hasAudio, hasVisual, touchReader, usesKeyboard, type Mode } from "../../store.ts";
import { announce } from "../../announce.ts";
import type { ClientGame, ClientGameModule, GameContext } from "../types.ts";
import type { Model } from "./model.ts";
import { SoundLayer } from "./sound-layer.ts";
import type { ProbStyle } from "./texts.ts";
import { pointsText } from "./texts.ts";
import { VisualLayer } from "./visual-layer.ts";

function setting(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

class KolonieClient implements ClientGame {
  private ctx: GameContext;
  private model: Model;
  private sound: SoundLayer | null = null;
  private visual: VisualLayer | null = null;

  constructor(ctx: GameContext, mode: Mode) {
    this.ctx = ctx;
    // Kurzor začíná uprostřed ostrova (řada 3, prostřední roh).
    const start = T.corners.findIndex((c) => c.row === 2 && c.col === 5);
    this.model = {
      me: ctx.me,
      view: null,
      send: (a) => ctx.sendAction(a),
      cursor: start >= 0 ? start : 0,
      edge: null,
      pick: null,
      log: [],
      since: [],
      prob: setting("ha.kolonie.prob", "cislo") as ProbStyle,
      confirmBuild: setting("ha.kolonie.confirm", "1") === "1",
    };
    this.setMode(mode);
  }

  setMode(mode: Mode) {
    if (hasAudio(mode) && !this.sound) this.sound = new SoundLayer(this.model, this.ctx.board, () => this.visual?.render());
    if (!hasAudio(mode) && this.sound) {
      this.sound.destroy();
      this.sound = null;
    }
    if (hasVisual(mode) && !this.visual) this.visual = new VisualLayer(this.model, this.ctx.board);
    if (!hasVisual(mode) && this.visual) {
      this.visual.destroy();
      this.visual = null;
    }
    if (this.visual) {
      this.visual.showCursor = usesKeyboard(mode);
      this.visual.render();
    }
    // Mapa Kolonie zatím pro dotykovou čtečku zpřístupněná není.
    if (touchReader(mode)) announce("Kolonie zatím v režimu Dotyk se čtečkou nefunguje. Na počítači zvol režim Zvuk.");
  }

  update(view: unknown, events: GameEvent[], initial: boolean) {
    this.model.view = view as KolonieView;
    this.renderStatus();
    if (!initial) {
      this.visual?.onEvents(events);
      this.sound?.onEvents(events);
    }
    this.visual?.render();
  }

  /** Stavový řádek jen „kdo je na tahu“; body a karty jsou v panelu hráčů (barvy odpovídají mapě). */
  private renderStatus() {
    const v = this.model.view!;
    const el = this.ctx.status;
    el.textContent = "";
    const turn = document.createElement("span");
    turn.className = "status-turn" + (v.turn === v.me && v.phase !== "over" ? " is-me" : "");
    turn.textContent = v.phase === "over" ? "Konec hry" : v.turn === v.me ? "Jsi na tahu" : `Na tahu je ${v.players[v.turn].name}`;
    el.appendChild(turn);
    if (v.dice && v.phase !== "setup") {
      const dice = document.createElement("span");
      dice.className = "status-score";
      dice.textContent = `Padlo ${v.dice[0] + v.dice[1]}`;
      el.appendChild(dice);
    }
  }

  rejected(reason: string) {
    this.sound?.rejected(reason);
  }

  resultText() {
    const v = this.model.view;
    if (!v || v.winner === null) return "";
    const who = v.winner === v.me ? "ty" : v.players[v.winner].name;
    return `Vítěz: ${who}. ` + v.players.map((p, i) => `${i === v.me ? "Ty" : p.name}: ${pointsText(i === v.me ? v.myPoints! : p.publicPoints)}`).join(", ") + ".";
  }

  destroy() {
    this.sound?.destroy();
    this.visual?.destroy();
  }
}

export const kolonieClient: ClientGameModule = {
  id: "kolonie",
  boardLabel: "Mapa Kolonie na Keplerovi. Šipky po rozích, S stavět, K hodit, U konec tahu, R stav, H nápověda.",
  mount: (ctx, mode) => new KolonieClient(ctx, mode),
};
